import {
    PlusOutlined
} from "@ant-design/icons";
import { Dropdown, Flex, Menu } from "antd";
import React, { useCallback, useContext, useState } from "react";
import Input from "../../components/custom/input/input/Input";
import HSelect from "../../components/custom/input/select/Select";
import HTableWithTabs from "../../components/custom/table/HTableWithTabs";
import { AppContext } from "../../context/AppContext";
import { DeleteComponent, EmptyListView } from "../utils/CommonVessels";
import { COMBINED_FILTER_OPTIONS, type UseWorkflowsResult, type WorkflowRecord } from "./hooks/useWorkflows";

export type { WorkflowApiRecord, WorkflowRecord, WorkflowTriggerType } from "./hooks/useWorkflows";

export interface TriggerRulesListProps {
    workflows: UseWorkflowsResult;
    onWorkflowClick: (workflow: WorkflowRecord) => void;
    onNewWorkflow: () => void;
}

/** Maps API status to list pill CSS (paused legacy + disabled share same style). */
function workflowStatusStyleKey(status: string): string {
    const s = (status ?? "").trim().toLowerCase();
    if (s === "paused" || s === "disabled") return "disabled";
    return s;
}

/** Human-readable status label (backend uses DISABLED; older rows may still be PAUSED). */
function workflowStatusDisplayLabel(status: string): string {
    const s = (status ?? "").trim().toLowerCase();
    if (s === "paused" || s === "disabled") return "Disabled";
    if (!status) return status;
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

const TriggerRulesList: React.FC<TriggerRulesListProps> = ({
    workflows,
    onWorkflowClick,
    onNewWorkflow,
}) => {
    const [appProperties] = useContext(AppContext);
    const {
        requestWorkflows,
        activeTab,
        searchInputValue,
        setSearchInputValue,
        appliedSearchKeyword,
        triggerTypeFilter,
        statusFilter,
        combinedFilterValue,
        handleFilterChange,
        handleSearchApply,
        handleSearchClear,
        handleTabChange,
        enableWorkflow,
        disableWorkflow,
        deleteWorkflow,
        tableRef,
    } = workflows;

    const [deleteModalRecord, setDeleteModalRecord] = useState<WorkflowRecord | null>(null);
    const [visibleDropdown, setVisibleDropdown] = useState<string | null>(null);

    const handleMenuClick = useCallback((record: WorkflowRecord, open: boolean) => {
        setVisibleDropdown(open ? record.workflowId : null);
    }, []);

    const handleDeleteConfirm = useCallback(
        async (record: WorkflowRecord) => {
            await deleteWorkflow(record);
            setDeleteModalRecord(null);
        },
        [deleteWorkflow]
    );


    const columns = [
        {
            title: "Workflow Name",
            dataIndex: "workflowName",
            key: "workflowName",
            width: "24%",
            render: (text: string) => <span className="hs-fs-14 hs-fw-600 hs-color-dark-grey">{text}</span>,
        },
        {
            title: "Trigger Type",
            dataIndex: "triggerType",
            key: "triggerType",
            width: "16%",
            render: (text: string): React.ReactNode => {
                const parts = (text ?? "").split(".");
                const middle = parts.length >= 2 ? parts[1] : text;
                return <span>{middle}</span>;
            },
        },
        {
            title: "Direction",
            dataIndex: "direction",
            key: "direction",
            width: "16%",
            render: (direction: WorkflowRecord["direction"]) => (
                <span
                    className={`d-inline-flex align-items-center gap-1 rounded-2 hs-fs-12 hs-fw-600 px-2 py-1 trigger-rules-list-direction-${direction.toLowerCase()}`}
                >
                    {direction === "Incoming" ? (
                        <div className="automationIconsSprite incoming-drirection-icon"></div>
                    ) : (
                        <div className="automationIconsSprite outgoing-direction-icon"></div>
                    )}
                    {direction}
                </span>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: "14%",
            render: (status: string) => {
                const styleKey = workflowStatusStyleKey(status ?? "");
                return (
                    <span
                        className={`d-inline-flex align-items-center gap-1 hs-fs-12 hs-fw-600 px-3 py-1 rounded-5 trigger-rules-list-status-${styleKey}`}
                    >
                        <span
                            className={`trigger-rules-list-status-dot rounded-circle trigger-rules-list-status-dot-${styleKey}`}
                        />
                        {workflowStatusDisplayLabel(status ?? "")}
                    </span>
                );
            },
        },
        {
            title: "Stats",
            dataIndex: "runs",
            key: "runs",
            width: "20%",
            render: (runs?: number) => (
                <span className="d-inline-flex align-items-baseline gap-1">
                    <span className="hs-fs-12 hs-fw-600 hs-color-dark-grey">{(runs ?? 0).toLocaleString()}</span>
                    <span className="hs-fs-12 hs-color-medium-grey">runs</span>
                </span>
            ),
        },
        ...(activeTab !== "predefined"
            ? [
                  {
                      title: "",
                      dataIndex: "workflowId",
                      key: "actions",
                      width: "10%",
                      align: "right" as const,
                      render: (_: unknown, record: WorkflowRecord): React.ReactNode => {
                          return (
                              <Dropdown
                                  overlay={WorkflowActionsMenu({ record, disableWorkflow, deleteWorkflow })}
                                  trigger={["click"]}
                                  placement="bottomRight"
                                  rootClassName="editDeleteSettingDropdown"
                                  open={visibleDropdown === record.workflowId}
                                  onOpenChange={(open) => handleMenuClick(record, open)}
                              >
                                  <div
                                      style={{ cursor: "pointer" }}
                                      className="actionIconsSprite editDeleteSettingsIcon"
                                      onClick={(e) => e.stopPropagation()}
                                  ></div>
                              </Dropdown>
                          );
                      },
                  },
              ]
            : []),
    ];

    const WorkflowActionsMenu = (props: { record: WorkflowRecord, disableWorkflow: (record: WorkflowRecord) => void, deleteWorkflow: (record: WorkflowRecord) => void }) => {
        const { record } = props;

        const statusNorm = (record.status ?? "").trim().toLowerCase();
        const showEnableInMenu =
            statusNorm === "paused" || statusNorm === "disabled" || statusNorm === "draft";

        return (
            <Menu>
              <Menu.Item
                key="disableWorkflow"
                onClick={(e) => {
                  e.domEvent.stopPropagation();
                  if (showEnableInMenu) {
                    enableWorkflow(record);
                  } else {
                    disableWorkflow(record);
                  }
                }}
              >
                <div className="actionIconsSprite editTemplate-icon">
                  <span className="ps-4" style={{ fontSize: "12px" }}>
                    {showEnableInMenu ? "Enable" : "Disable"}
                  </span>
                </div>
              </Menu.Item>
              <Menu.Item
                key="deleteWorkflow"
                onClick={(e) => {
                  e.domEvent.stopPropagation();
                  deleteWorkflow(record);
                }}
              >
                <div className="actionIconsSprite deleteTemplate-icon">
                  <span className="ps-4" style={{ fontSize: "12px" }}>
                    Delete
                  </span>
                </div>
              </Menu.Item>
            </Menu>
        );
      };

    const hTableWithTabsProps = {
        tableRef,
        tabMenuItems: [
            { key: "custom", label: "Custom" },
            { key: "predefined", label: "Pre-Defined Flows" },
        ] as const,
        defaultActiveKey: activeTab,
        onChange: handleTabChange,
        ui: {
            columns,
            rowKey: "workflowId",
            heightToBeSubtractedFromContainerHeight: 50,
            title: (
                <Flex align="center" gap={20}>
                    <HSelect
                        options={COMBINED_FILTER_OPTIONS}
                        value={combinedFilterValue}
                        onChange={handleFilterChange}
                        placeholder="Filter by trigger & status"
                        customProps={{ className: "mt-1", style: { width: 200 } }}
                    />
                    <Input
                        placeholder="Search by name"
                        allowClear
                        style={{ width: 200 }}
                        value={searchInputValue}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSearchInputValue(e.target.value);
                            if (!e.target.value?.trim()) {
                                handleSearchClear();
                            }
                        }}
                        onPressEnter={handleSearchApply}
                    />
                </Flex>
            ),
        },
        data: {
            request: requestWorkflows,
            requestExtraParams: {
                searchKeyword: appliedSearchKeyword,
                flowType: activeTab,
                triggerType: triggerTypeFilter,
                status: statusFilter,
            },
        },
        toolbar: {
            buttonText: "New Trigger Rule",
            onButtonClick: activeTab === "custom" ? onNewWorkflow : undefined,
            buttonIcon: <PlusOutlined />,
        },
        proProps: {
            locale: {
                emptyText: (
                    <EmptyListView
                        icon=""
                        tagLineOne="No workflows yet"
                        tagLineTwo="Create your first trigger rule automation"
                        tagHeight="32vh"
                    />
                ),
            },
        },
        onRowClick: (record: WorkflowRecord) => ({
            onClick: () => onWorkflowClick(record),
            style: { cursor: "pointer" },
        }),
    };

    return (
        <Flex className="flex-column h-100 overflow-hidden rounded-2 p-3">
            <HTableWithTabs {...hTableWithTabsProps} />

            {deleteModalRecord && (
                <DeleteComponent
                    modalOpen={!!deleteModalRecord}
                    setModalOpen={(open: boolean) => !open && setDeleteModalRecord(null)}
                    record={deleteModalRecord}
                    confirmDeleteIcon="hs-delete-icon"
                    confirmDeleteBtnName="Delete"
                    confirmDeleteComponentTitle="Delete workflow?"
                    confirmDeleteComponent="Are you sure you want to delete this workflow? This action cannot be undone."
                    appProperties={appProperties}
                    handleDelete={() => handleDeleteConfirm(deleteModalRecord)}
                />
            )}
        </Flex>
    );
};

export default TriggerRulesList;
